import type { EarlyStageVerificationStatus } from "../../api/earlyStageMembershipApi";
import { isEarlyStageMembership } from "../../utils/membershipTier";

export type RegistrationUserSnapshot = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  membershipName?: string | null;
  isTermConditions?: number | null;
  hasPaid?: number | null;
  earlyStagePriceTier?: string | null;
};

/** Pending under-23 review but user chose to proceed at £10/month now. */
export function earlyStagePendingProceedingAtStandard(
  status: EarlyStageVerificationStatus | null,
  user?: Pick<RegistrationUserSnapshot, "earlyStagePriceTier"> | null,
): boolean {
  const s = (status?.status ?? "").trim();
  if (s !== "Pending" && s !== "UnderReview") return false;
  const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
  return tier === "Premium10";
}

export function hasRegistrationPersonalInfoSaved(
  user: RegistrationUserSnapshot,
): boolean {
  return Boolean(user.firstName?.trim() && user.email?.trim());
}

export function isEarlyStageVerificationInProgress(
  status: EarlyStageVerificationStatus | null,
): boolean {
  const s = (status?.status ?? "").trim();
  return (
    s === "Pending" ||
    s === "UnderReview" ||
    s === "Approved" ||
    s === "Rejected" ||
    s === "Expired"
  );
}

/** User chose or was placed on the standard £10/month Early Stage path (not under-23). */
export function isEarlyStageStandardTenPoundPathCommitted(
  status: EarlyStageVerificationStatus | null,
  user?: Pick<RegistrationUserSnapshot, "earlyStagePriceTier"> | null,
): boolean {
  const verificationStatus = (status?.status ?? "").trim();
  const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
  const ageTier = (status?.ageTier ?? "").trim();

  if (earlyStagePendingProceedingAtStandard(status, user)) return true;

  if (verificationStatus === "Rejected" && tier === "Premium10") return true;

  return (
    verificationStatus === "Approved" &&
    tier === "Premium10" &&
    (ageTier === "TwentyThreePlus" || ageTier === "23Plus")
  );
}

/** Approved under-23 who still must pick £2 or £10 before terms/checkout. */
export function needsEarlyStagePricePlanSelection(
  status: EarlyStageVerificationStatus | null,
): boolean {
  if ((status?.status ?? "").trim() !== "Approved") return false;
  const priceTier = status?.priceTier?.trim() ?? "";
  if (priceTier === "Standard2" || priceTier === "Premium10") return false;
  return Boolean(status?.canSelectPriceTier);
}

/**
 * Early Stage only (7 steps). Uses DB user row only — not localStorage draft.
 * 0 personal → 1 membership → 2 age verification → 3 payment plan (under 23) →
 * 4 terms → 5 checkout → 6 confirmation
 */
export function resolveEarlyStageRegistrationStep(
  user: RegistrationUserSnapshot,
  status: EarlyStageVerificationStatus | null,
): number {
  if (user.hasPaid === 1) return 6;

  const verificationStatus = (status?.status ?? "").trim();

  if (verificationStatus === "Pending" || verificationStatus === "UnderReview") {
    if (earlyStagePendingProceedingAtStandard(status, user)) {
      if (user.isTermConditions !== 1) return 4;
      return 5;
    }
    return 2;
  }
  if (verificationStatus === "Rejected") {
    const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
    if (tier === "Premium10") {
      if (user.isTermConditions !== 1) return 4;
      return 5;
    }
    return 2;
  }
  if (verificationStatus === "Approved") {
    const tier = (status?.priceTier ?? user?.earlyStagePriceTier ?? "").trim();
    const ageTier = (status?.ageTier ?? "").trim();
    if (needsEarlyStagePricePlanSelection(status)) {
      return 2;
    }
    if (
      tier === "Premium10" &&
      (ageTier === "TwentyThreePlus" || ageTier === "23Plus")
    ) {
      if (user.isTermConditions === 1) return 5;
      return 4;
    }
    if (user.isTermConditions === 1) return 5;
    return 2;
  }

  if (!hasRegistrationPersonalInfoSaved(user)) return 0;
  if (!isEarlyStageMembership(user.membershipName)) return 1;

  return 1;
}

/** Standard tiers (5 steps). Uses DB user row only. */
export function resolveStandardRegistrationStep(
  user: RegistrationUserSnapshot,
): number {
  if (user.hasPaid === 1) return 4;
  if (!hasRegistrationPersonalInfoSaved(user)) return 0;
  if (!user.membershipName?.trim()) return 1;
  if (user.isTermConditions !== 1) return 2;
  return 3;
}
